import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { inr } from "../lib/api";

export default function CartDrawer() {
    const { open, setOpen, items, subtotal, shipping, total, setQty, remove } = useCart();
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
            <div className="absolute inset-0 bg-ink-900/40" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-ivory-100 flex flex-col animate-fade-up">
                <div className="flex items-center justify-between px-6 py-5 border-b border-sage-300/30">
                    <h3 className="font-serif text-2xl text-ink-900">Your Bag <span className="text-ink-500 text-base">({items.length})</span></h3>
                    <button onClick={() => setOpen(false)} aria-label="Close" data-testid="cart-close-btn"><X /></button>
                </div>

                {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center text-ink-500">
                        <ShoppingBag size={32} strokeWidth={1.2} className="text-sage-400 mb-3" />
                        <p className="font-serif text-2xl text-ink-900">Your bag is empty</p>
                        <p className="text-sm mt-2">Add a few favourites to begin.</p>
                        <Link to="/shop" onClick={() => setOpen(false)} className="btn-primary mt-6">Discover Pieces</Link>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-5 divide-y divide-sage-300/20">
                            {items.map((i) => (
                                <div key={`${i.productId}-${i.size}`} className="flex gap-4 py-4" data-testid={`cart-item-${i.slug}`}>
                                    <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="h-24 w-20 rounded-xl overflow-hidden bg-white shrink-0">
                                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                    </Link>
                                    <div className="flex-1">
                                        <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="font-serif text-lg text-ink-900 hover:text-sage-700">{i.name}</Link>
                                        {i.size && <p className="text-xs text-ink-500 mt-0.5">Size: {i.size}</p>}
                                        <p className="text-sm text-sage-700 mt-1">{inr(i.price)}</p>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="inline-flex items-center gap-2 bg-white rounded-full px-2 py-1 border border-sage-300/30">
                                                <button onClick={() => setQty(i.productId, i.size, i.qty - 1)} className="h-7 w-7 flex items-center justify-center text-ink-700" data-testid={`cart-qty-minus-${i.slug}`}><Minus size={12} /></button>
                                                <span className="text-sm w-5 text-center">{i.qty}</span>
                                                <button onClick={() => setQty(i.productId, i.size, i.qty + 1)} className="h-7 w-7 flex items-center justify-center text-ink-700" data-testid={`cart-qty-plus-${i.slug}`}><Plus size={12} /></button>
                                            </div>
                                            <button onClick={() => remove(i.productId, i.size)} className="text-ink-500 hover:text-blush-400" data-testid={`cart-remove-${i.slug}`}><Trash2 size={15} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-sage-300/30 px-6 py-5 bg-ivory-50">
                            <div className="space-y-1.5 text-sm">
                                <p className="flex justify-between text-ink-700"><span>Subtotal</span><span>{inr(subtotal)}</span></p>
                                <p className="flex justify-between text-ink-700"><span>Shipping</span><span>{shipping === 0 ? <span className="text-sage-600">Free</span> : inr(shipping)}</span></p>
                                {shipping > 0 && (
                                    <p className="text-xs text-sage-600 italic pt-1">Add {inr(1500 - subtotal)} more for free shipping ✨</p>
                                )}
                                <p className="flex justify-between text-ink-900 pt-2 text-lg font-serif"><span>Total</span><span>{inr(total)}</span></p>
                            </div>
                            <Link to="/checkout" onClick={() => setOpen(false)} className="btn-primary w-full mt-5" data-testid="cart-checkout-btn">Checkout</Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
